import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import Stack from 'react-bootstrap/Stack';
import Accordion from 'react-bootstrap/Accordion';
import Swal from 'sweetalert2';
import moment from 'moment';

import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { addAction, getAllActions, getTimeline } from '../../../../store/ai2';

import pinkBellIcon from '../../../../assets/img/pinkBell.png';
import purpleBellIcon from '../../../../assets/img/purpleBell.png';

import '../styles/ActionItem.scss';
import { ActionLog } from './ActionLog';

function CustomToggle({ eventKey }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const decoratedOnClick = useAccordionButton(eventKey, () => {
    setIsExpanded(!isExpanded);
  });

  return (
    <div onClick={() => decoratedOnClick()}>
      <span className="arrowCollapse" style={{ marginRight: 10 }}>
        <i
          className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} fa-lg`}
        ></i>
      </span>
    </div>
  );
}

function ActionItemComponent() {
  const [dropdownOpen, setDropdownOpen] = useState(true);
  const [textareaValue, setTextareaValue] = useState('');
  const [actionTitle, setActionTitle] = useState([]);
  const [selectedClinician, setSelectedClinician] = useState('');
  const { actions, lastUpdatedAction } = useSelector((state) => state.actions);
  const { firstName, lastName, accessToken, clinicianId, selectedTenancy } =
    useSelector((state) => state.clinicianProfile);
  const clinician_clinics = useSelector(
    (state) => state.clinicianProfile.allTenancyClinics,
  );
  const patient_id = useSelector(
    (state) => state.patientProfile.patientProfile.id,
  );
  const isLoading = useSelector((state) => state.patientProfile.loading);
  const patient_clinics = useSelector(
    (state) => state.patientProfile.patientProfile.clinics,
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');

  useEffect(() => {
    if (actions && actions.length > 0) {
      // Mapping the clinician_ids into an object using the action id as the key
      const clinicianObj = actions.reduce((acc, action, index) => {
        acc[index] = action.access_token;
        return acc;
      }, {});
      const descriptionObj = actions.reduce((acc, action, index) => {
        acc[index] = action.description;
        return acc;
      }, {});
      setSelectedClinician(clinicianObj);
      setActionTitle(descriptionObj);
    }
  }, [actions]);

  let patientClinicsIds = new Set();
  // Need to have this to handle the asynchronous issue
  if (!isLoading && patient_clinics) {
    patientClinicsIds = new Set(
      Object.values(patient_clinics).map((clinic) => clinic.id),
    );
  }

  // Filter clinician clinics based on patient clinic IDs
  const filteredClinicianClinics = Object.keys(clinician_clinics)
    .filter((key) => patientClinicsIds.has(clinician_clinics[key].clinic_id))
    .reduce((result, key) => {
      result[key] = clinician_clinics[key];
      return result;
    }, {});
  const combinedClinicians = Object.values(filteredClinicianClinics).reduce(
    (acc, clinicObj) => acc.concat(clinicObj.caremapprClinicians),
    [],
  );

  const uniqueIds = new Set();

  const uniqueClinicians = combinedClinicians.filter((clinician) => {
    if (!uniqueIds.has(clinician.access_token)) {
      uniqueIds.add(clinician.access_token);

      return true;
    }
    return false;
  });

  const dispatch = useDispatch();
  const [localActions, setLocalActions] = useState([]);

  useEffect(() => {
    setLocalActions(
      actions.filter(
        (action) =>
          action.action_status === null || action.action_status === 'R',
      ),
    );
  }, [actions]);

  const actionOptions = [
    'Face-to-face clinic',
    'Phone contact',
    'Face-to-face home visit',
    'Provider interaction',
    'Carer interaction',
    'Other',
  ];

  const [actionStatus, setActionStatus] = useState(null);
  function handleClinicianSelection(index, value) {
    const updatedClinicians = { ...selectedClinician, [index]: value };
    setActionStatus('R');
    setSelectedClinician(updatedClinicians);
  }

  function handleTextareaChange(index, value) {
    const updatedTextareaValues = { ...textareaValue, [index]: value };
    setTextareaValue(updatedTextareaValues);
  }

  function handleActionTitleChange(index, value) {
    const updatedActionTitles = { ...actionTitle, [index]: value };
    setActionTitle(updatedActionTitles);
  }

  function handleCancelAccordion(index) {
    const updatedClinicians = { ...selectedClinician };
    delete updatedClinicians[index];
    setSelectedClinician(updatedClinicians);

    const updatedTextareaValues = { ...textareaValue };
    delete updatedTextareaValues[index];
    setTextareaValue(updatedTextareaValues);
  }

  const handleAddAction = () => {
    const newAction = {
      id: undefined,
      isNew: true,
      action_log: [],
      action_status: null, // Example status
      actioned_by: { first_name: firstName, last_name: lastName }, // Example name
      clinician_id: '', // Example ID
      created_at: new Date().toISOString(),
      description: '-Select an action-', // Example description
      ended_at: '', // Example end date
      name: '',
      patient_id: 4, // Example patient ID
      started_at: new Date().toISOString(), // Example start date
      who_was_assigned: '', // Example who_actioned ID
    };

    setLocalActions([newAction].concat(localActions));

    dispatch(
      getTimeline(
        access_token,
        moment().add(-2, 'year').format(),
        moment().add(0, 'year').format(),
        selectedTenancy,
        true,
      ),
    );

    setTimeout(() => {
      // Assuming the new item is always the first child of the accordion
      const newAccordionItem = document.querySelector(
        '.actions-accordion .accordion-item:last-child .accordion-collapse',
      );
      if (newAccordionItem) {
        newAccordionItem.classList.add('show');
      }
    }, 0);
  };

  const handleActionSubmit = async (actionId, index, action_status) => {
    if (!selectedClinician[index] || !actionTitle[index]) {
      Swal.fire({
        icon: 'warning',
        text: 'Select a clinician and a title for the action',
        showConfirmButton: true,
      });
      return;
    }

    // Form data to submit
    const formData = {
      access_token: access_token,
      actionId: actionId,
      description: actionTitle[index],
      reason: action_status === null ? 'Set to Ongoing' : textareaValue[index],
      action_status: action_status,
      who_was_assigned: selectedClinician[index],
    };

    // Here you can dispatch the form data to your store or send it to the server as needed
    await dispatch(await addAction(formData, selectedTenancy));
    await dispatch(await getAllActions(access_token, selectedTenancy));

    Swal.fire({
      icon: 'success',
      text: 'Action has been saved',
      showConfirmButton: true,
    });
  };

  return (
    <Fragment>
      <Button
        className={'add_action_button'}
        type="button"
        onClick={handleAddAction}
      >
        + New action
      </Button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {localActions.length > 0 && (
          <h2 className="heading2">Current actions</h2>
        )}
      </div>
      <div className={'red'}>
        <Accordion
          className="actions-accordion"
          id="currentActions"
          defaultActiveKey={lastUpdatedAction}
          alwaysOpen={dropdownOpen}
        >
          {[...localActions].reverse().map((item, index) => (
            <Accordion.Item
              key={index}
              eventKey={index}
              className={
                'accordion-item ' +
                (item.clinician_id === clinicianId ? 'pink' : 'purple')
              }
            >
              <Accordion.Header>
                <CustomToggle
                  eventKey={index}
                  isDropdownOpen={dropdownOpen}
                ></CustomToggle>
                <div>
                  {item.clinician_id === clinicianId && (
                    <img
                      className="bell-icon"
                      style={{ width: 17, height: 17, margin: 5 }}
                      src={pinkBellIcon}
                      alt="pink bell"
                    ></img>
                  )}
                  {item.clinician_id !== clinicianId && (
                    <img
                      className="bell-icon"
                      style={{ width: 17, height: 17, margin: 5 }}
                      src={purpleBellIcon}
                      alt="purple bell"
                    ></img>
                  )}
                </div>
                <div id="why_created" className="action-name">
                  <select
                    value={actionTitle[index] || item.description}
                    onChange={(e) => {
                      handleActionTitleChange(index, e.target.value);
                      setDropdownOpen(false);
                    }}
                    onClick={(e) => {
                      setDropdownOpen(true); // Set dropdownOpen to true when the dropdown is clicked
                      e.stopPropagation(); // Prevent the event from bubbling up
                    }}
                  >
                    <option>{item.description}</option>
                    {actionOptions.map((option, idx) => (
                      <option key={idx} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </Accordion.Header>

              <Accordion.Body>
                <div className="flexHorizWrap mb-4">
                  <div className="flexItem" style={{ maxWidth: 70 }}>
                    <label className="labelGrey labelBold">
                      {item.action_status === 'E' ||
                      item.action_status === 'A' ||
                      item.action_status === 'D' ||
                      item.action_status === 'C' ||
                      item.action_status === 'S'
                        ? 'Closed'
                        : item.action_status === null ||
                            item.action_status === 'R'
                          ? 'Ongoing'
                          : item.action_status === 'I'
                            ? 'Ignored'
                            : 'no data'}
                    </label>
                  </div>
                  <div className="flexItem">
                    <label className="labelGrey labelSimple">
                      {item.created_at
                        ? moment(new Date(item.created_at)).format(
                            'DD MMM YYYY',
                          ) + ' - '
                        : ''}
                      {item.action_status === 'C' && item.ended_at
                        ? moment(new Date(item.ended_at)).format('DD MMM YYYY')
                        : ''}
                    </label>
                  </div>
                </div>
                <div className="actionContainer">
                  <div className="flexItem">
                    <Form id="actionForm">
                      <div id="txt3" className="flexVert divSelect">
                        <div className="flexVertWrap mb-4">
                          <label
                            className="txtLeft txtDropdownLabel"
                            style={{ whiteSpace: 'normal' }}
                          >
                            Assigned to:
                          </label>
                          <Form.Select
                            id={'clinicianOptions'}
                            className={'insightSelect'}
                            value={selectedClinician[index] || '-Select-'}
                            onChange={(e) =>
                              handleClinicianSelection(index, e.target.value)
                            }
                          >
                            <option value="-Select-" disabled>
                              -Select a Clinician-
                            </option>
                            {uniqueClinicians.map((clinician, index) => (
                              <option
                                key={index}
                                value={clinician.access_token}
                              >
                                {`${clinician.first_name} ${clinician.last_name}`}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        <div className="flexVertWrap mb-4">
                          <label
                            className="txtLeft txtDropdownLabel"
                            style={{ whiteSpace: 'normal' }}
                          >
                            Action update
                          </label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            id="insightReasonTextbox"
                            name="actionUpdate"
                            value={textareaValue[index] || ''}
                            onChange={(e) => {
                              handleTextareaChange(index, e.target.value);
                            }}
                            placeholder="Enter action update..."
                          />
                        </div>
                      </div>
                    </Form>
                  </div>
                  <div className="actionLog">
                    <ActionLog item={item}></ActionLog>
                  </div>
                </div>
                <Stack direction="horizontal" gap={2}>
                  <button
                    style={{ marginRight: 15 }}
                    type="button"
                    className="btn btnSecondary btn-xs btn-long ms-auto"
                    onClick={() => handleActionSubmit(item.id, index, 'C')}
                  >
                    Complete Action
                  </button>
                  <button
                    type="button"
                    className="btn btnSecondary btn-long btn-xs"
                    onClick={() => handleCancelAccordion(index)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btnPrimary btn-xs btn-long"
                    onClick={() =>
                      handleActionSubmit(item.id, index, actionStatus)
                    }
                  >
                    Save
                  </button>
                </Stack>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    </Fragment>
  );
}

export { ActionItemComponent };
